// Lightweight analytics tracker.
// - Persistent `session_id` per pageload (sessionStorage) so a single
//   visit rolls up on the backend even when the user routes through 5
//   pages. Cleared on tab close.
// - `trackView(feature)` — pair with the returned function to close the
//   view and log the duration on unmount.
// - `trackAction(feature, event, meta)` — fire-and-forget action row.
// - `heartbeat(feature)` — optional 30 s heartbeat while a screen stays
//   open (used by the AnalyticsProvider hook).
// Failure is silent — analytics must never break the UX.

import axios from "axios";

const SESS_KEY = "yacht_analytics_sid_v1";
const IPAD_TOKEN_KEY = "yacht_ipad_session";
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

function newSessionId() {
  return "s_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

function getSessionId() {
  try {
    let sid = sessionStorage.getItem(SESS_KEY);
    if (!sid) { sid = newSessionId(); sessionStorage.setItem(SESS_KEY, sid); }
    return sid;
  } catch {
    return newSessionId();
  }
}

function actorHint() {
  // Dashboard identity is authenticated server-side by the HttpOnly cookie.
  // The path hint only selects the correct analytics endpoint; it does not
  // grant access because /admin/analytics/track still validates the cookie.
  try {
    const p = window.location.pathname || "";
    if (p.startsWith("/admin") || p.startsWith("/crew") || p.startsWith("/mgmt") || p.startsWith("/spa-ops"))
      return { actor_kind: "dashboard" };
  } catch { /* SSR */ }
  try {
    const ipadTok = localStorage.getItem(IPAD_TOKEN_KEY);
    if (ipadTok) return { actor_kind: "ipad", ipad_token: ipadTok };
  } catch { /* private mode */ }
  // Non-iPad guests → phone.
  try {
    if (localStorage.getItem("yacht_guest_id")) return { actor_kind: "phone" };
  } catch { /* ignore */ }
  return { actor_kind: "phone" };
}

// Kept for API compatibility with older callers; the tracker now reads
// the token synchronously via localStorage in `actorHint`, so this
// setter is a no-op after the fix.
export function setDashboardAuthToken(_token) { /* deprecated */ }

async function postEvent(url, body, headers) {
  try {
    await axios.post(`${BACKEND}${url}`, body, { headers, timeout: 4000, withCredentials: true });
  } catch (e) {
    // Analytics MUST never surface as a toast — swallow.
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[analytics] drop:", e?.response?.status, url);
    }
  }
}

function isEnabledOnClient() {
  // Manager can flip analytics off via settings — the /public/settings
  // response carries `analytics_enabled` and each guest page primes it
  // via `prime`. Default true so a first-time device still records.
  try {
    const raw = localStorage.getItem("api::/public/settings");
    if (!raw) return true;
    const parsed = JSON.parse(raw);
    return parsed?.data?.analytics_enabled !== false;
  } catch {
    return true;
  }
}

export function trackEvent({ feature, action = "action", duration_ms = 0, meta = null, path = null }) {
  if (!isEnabledOnClient()) return;
  const sid = getSessionId();
  const actor = actorHint();
  const body = {
    session_id: sid,
    actor_kind: actor.actor_kind,
    feature: feature || "unknown",
    action,
    duration_ms,
    path: path || (typeof window !== "undefined" ? window.location.pathname : null),
    meta: meta || null,
    at_client: new Date().toISOString(),
  };
  if (actor.actor_kind === "dashboard") {
    return postEvent("/api/admin/analytics/track", body, {});
  }
  const headers = actor.ipad_token ? { "X-IPad-Token": actor.ipad_token } : {};
  return postEvent("/api/public/analytics/track", body, headers);
}

// trackView returns a `close(meta)` fn — call in useEffect cleanup so the
// view duration is captured accurately even on unmount / route change.
export function trackView(feature, extraMeta = null) {
  const enteredAt = Date.now();
  trackEvent({ feature, action: "view_enter", duration_ms: 0, meta: extraMeta });
  return function close(exitMeta = null) {
    const duration_ms = Math.max(0, Date.now() - enteredAt);
    trackEvent({ feature, action: "view_exit", duration_ms, meta: exitMeta });
  };
}

export function trackAction(feature, event, meta = null) {
  trackEvent({ feature, action: "action", meta: { ...(meta || {}), event } });
}

// React hook — usage:  useTrackView("menu");
// Handles enter on mount + exit on unmount + heartbeat every 30s.
// StrictMode: React 18 mounts, unmounts and remounts each hook synchronously
// on the same instance in development. A naïve ref-based dedupe fails
// because the cleanup runs between the two mounts and resets the guard.
// We defer the exit fire onto the next task; if the same feature has
// already been re-registered by the second StrictMode mount by then,
// we skip the spurious view_exit AND we skip the re-enter (the second
// mount's `entered` symbol is stashed but no new row is written).
import { useEffect, useRef } from "react";
const activeViews = new Map(); // feature -> Symbol (last mount's owner)
export function useTrackView(feature, extraMeta = null) {
  const ownerRef = useRef(null);
  useEffect(() => {
    if (!feature) return; // no-op until the caller supplies a real feature
    const owner = Symbol("view");
    ownerRef.current = owner;
    const previousOwner = activeViews.get(feature);
    activeViews.set(feature, owner);
    const enteredAt = Date.now();
    // Only fire view_enter if this is the *first* live owner for the
    // feature (i.e. no StrictMode remount is racing us). We defer via a
    // microtask so the cleanup below can win the race first.
    let entered = false;
    const enterTimer = setTimeout(() => {
      if (activeViews.get(feature) === owner) {
        trackEvent({ feature, action: "view_enter", meta: extraMeta });
        entered = true;
      }
    }, 0);
    const hb = setInterval(() => {
      trackEvent({ feature, action: "heartbeat", duration_ms: 30000 });
    }, 30000);
    const onHide = () => {
      if (document.visibilityState === "hidden" && entered) {
        trackEvent({ feature, action: "view_exit", duration_ms: Math.max(0, Date.now() - enteredAt) });
        clearInterval(hb);
        entered = false;
      }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => {
      clearInterval(hb);
      clearTimeout(enterTimer);
      document.removeEventListener("visibilitychange", onHide);
      setTimeout(() => {
        // If a re-mount replaced our owner in the meantime, this was a
        // StrictMode fake unmount — don't emit anything.
        if (activeViews.get(feature) !== owner) return;
        activeViews.delete(feature);
        if (!entered) return; // nothing to close
        trackEvent({ feature, action: "view_exit", duration_ms: Math.max(0, Date.now() - enteredAt) });
      }, 0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature]);
}
